/* eslint-disable no-unused-vars */
import moment from "moment";
import { any, func, string, bool, array } from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { TextAreaGroup } from "..";
import Checkbox from "../Checkbox/index";
import { Header } from "../../components/BasicInfo";
import HelpIcon from "../HelpIcon";
import OptionsButton from "../OptionsButton";
import OverflowedText from "../OverflowedText";
import SelectListGroup from "../SelectListGroup";
import TextFieldGroup from "../TextFieldGroup";
import styles from "./styles.scss";
import RadioButton from "../RadioButton";
import Table from "./table";
import Spinner from "../Spinner";
import HorizontalLine from "./HorizontalLine";

const FormCreatorV2 = ({
  formTitle,
  description,
  fields,
  setField,
  handleSubmit,
  handleCancel,
  onboardingBranch,
  helper = "All fields marked with an asterisk* are required",
  customHtml,
  handleSave,
  isCustomSaveButton,
  disableSave,
  submitDisabled,
  loading,
  workHistoryData = [],
  setValue,
  removeLast,
  validationErrorMessage,
  fiveYearsErrorMessage,
  xYearsWorkErrorMessage,
  setWorkHistoryData,
  shouldDisable,
  shouldDisableAddress,
  repeatedContent,
  repeatedDisable,
  addressHistoryData = [],
  setAddressHistoryData,
  addressYearsErrorMessage,
  addressValidationErrorMessage,
  workValidationErrorMessage,
  setAddressHistoryValue,
  addressHistoryRemoveLast,
  submitSingleForm,
  handleSingleSubmit,
  useSubmitText,
  workHistoryDataTitle,
}) => {
  /*
  Component creator to ease the pain of creating very similar forms
  Form is created based on initialState object you put in through props
  'fields', and 'setField' are state and change handler of useForm hook
  */

  const checkIfShouldShow = (dependOn, display, dependOnAny, dependOnExact) => {
    if (display === false) {
      return false;
    }
    if (dependOn) {
      return dependOn.every((dep) => {
        const { fieldKey, allowedValues } = dep;
        const actualValue = fields[fieldKey].value;

        if (allowedValues) {
          return typeof actualValue === "object"
            ? actualValue[allowedValues]
            : allowedValues && allowedValues.includes(actualValue);
        }
        if (typeof actualValue === "object") return actualValue[allowedValues];
        if (typeof actualValue === "string") return !!actualValue;
        return (
          actualValue === true ||
          (allowedValues && allowedValues.includes(actualValue))
        );
      });
    }

    if (dependOnAny) {
      return dependOnAny.some((dep) => {
        const { fieldKey, allowedValues } = dep;
        const actualValue = fields[fieldKey].value;

        if (allowedValues) {
          return typeof actualValue === "object"
            ? actualValue[allowedValues]
            : allowedValues && allowedValues.includes(actualValue);
        }

        return typeof actualValue === "object"
          ? actualValue[allowedValues]
          : actualValue === true ||
              (allowedValues && allowedValues.includes(actualValue));
      });
    }

    if (dependOnExact) {
      const list = dependOnExact[0];
      const exactNumber = dependOnExact[1];
      const trueValues = list.filter((el) => !!fields[el.fieldKey].value);
      return trueValues.length >= exactNumber;
    }

    return true;
  };

  const showAstrix = (el) => {
    if (
      el.type === "ucb" ||
      el.type === "uncb" ||
      el.type === "travelling" ||
      el.type === "studying"
    ) {
      return "";
    }
    return "*";
  };

  const inputStyle = { width: "20px", height: "20px", marginRight: "5px" };

  let listKeysMissed = [];

  const mandatoryFieldsCompletedCheck = () => {
    listKeysMissed = [];

    const validatedList = Object.entries(fields)
      .filter(
        ([_, { mandatory, dependOn, dependOnAny, dependOnExact }]) =>
          checkIfShouldShow(dependOn, undefined, dependOnAny, dependOnExact) &&
          mandatory
      )
      .map(([key, { value, type }]) => {
        if (typeof value === "string") {
          const isCorrect = value !== "";
          if (!isCorrect) listKeysMissed.push(key);
          return isCorrect;
        }
        if (type === "singlecheckbox") {
          return value;
        }
        // this is sufficient as the typeof undefined is not boolean
        if (typeof value === "boolean") {
          return true;
        }

        if (type === "multivalueCheckbox") {
          const isCorrect = Object.values(value).some(
            (checkbox) => checkbox === true
          );
          if (!isCorrect) listKeysMissed.push(key);
          return isCorrect;
        }
        if (type === "dob") {
          const isCorrect =
            value.days !== undefined &&
            value.months !== undefined &&
            value.year !== undefined;
          if (!isCorrect) listKeysMissed.push(key);
          return isCorrect;
        }
        listKeysMissed.push(key);
        return false;
      });
    return validatedList.every((e) => !!e);
  };

  const validationsPass = Object.entries(fields)
    .filter(([_, { validate }]) => validate)
    .every(([_, { value, validate }]) => validate(value));

  const isSubmittable = mandatoryFieldsCompletedCheck() && validationsPass;

  const checkWorkHistory = workHistoryData.length > 0;
  const checkRepeatedDisabled = repeatedDisable && repeatedDisable.length > 0;

  const wipeNotShownFieldsBeforeSubmit = () => {
    const keysToWipe = Object.entries(fields)
      .filter(([_, { dependOn }]) => !checkIfShouldShow(dependOn))
      .map(([key]) => key);

    setField(keysToWipe, "");
  };

  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isSubmitting) {
      handleSubmit();
      setSubmitting(false);
    }
  }, [isSubmitting]);

  const submit = () => {
    setSubmitting(true);
    wipeNotShownFieldsBeforeSubmit();
  };

  // const [firstAttempt, setFirstAttempt] = useState(true);

  const onFieldChange = () => {
    // if (!firstAttempt) {
    mandatoryFieldsCompletedCheck();
  };

  const renderField = ([
    key,
    {
      title,
      value,
      type = "text",
      dependOn,
      dependOnAny,
      mandatory,
      htmlObj,
      hint,
      checkboxText1,
      checkboxText2,
      validate,
      selectOptions,
      optionsDetailsInputs,
      radioOptions,
      textDescription,
      tableDetails,
      display,
      template,
      disabled = false,
      seeAsterix = true,
      style,
      inputWidth,
      dependOnExact,
      maxTableRowLength,
      tableNumOfMandatoryRows,
      repeatedIndex,
      workHistoryYrs,
      workHistoryColumns,
      initialWorkHistory,
      addressHistoryColumns,
      initialAddressHistory,
      onlyPrevious,
      rowTitle,
      rows,
      customFunc = () => {},
    },
  ]) => {
    const ignoredDnataOptions = [
      "A White:",
      "B Mixed:",
      "C Asian, Asian British, Asian English, Asian Scottish or Asian Welsh:",
      "D Black, Black British, Black English, Black Scottish, or Black Welsh:",
      "E Chinese, Chinese British, Chinese English, Chinese Scottish, Chinese Welsh, or other ethnic group:",
    ];
    if (checkIfShouldShow(dependOn, display, dependOnAny, dependOnExact)) {
      return (
        <div className="form-group tw-mb-5" style={style} key={key}>
          <div>
            {htmlObj ||
              (type !== "singlecheckbox" && type !== "heading" && title && (
                <label htmlFor={key} className={`mb-hf`}>
                  {title}
                  {mandatory && seeAsterix ? "*" : ""}
                  {hint ? (
                    <OverflowedText
                      forceOverflow
                      overflowedText={hint}
                      styles={{ display: "inline" }}
                    >
                      <HelpIcon className="ml-hf" />
                    </OverflowedText>
                  ) : (
                    ""
                  )}
                </label>
              ))}
          </div>
          {value !== undefined &&
            ["text", "number"].includes(type) &&
            (template ? (
              template(
                <TextFieldGroup
                  inputId={key}
                  classes={`register_field d-inline col-9 col-sm-6 col-lg-5 ${
                    validate && !validate(value) ? styles.hasError : ""
                  }`}
                  type={type}
                  value={value}
                  onChange={(e) => {
                    setField(key, e.target.value);
                    onFieldChange();
                  }}
                  inputWidth={inputWidth}
                />
              )
            ) : (
              <TextFieldGroup
                inputId={key}
                classes={`register_field ${
                  validate && !validate(value) ? styles.hasError : ""
                }`}
                type={type}
                value={value}
                isDisabled={disabled}
                inputWidth={inputWidth}
                onChange={(e) => {
                  setField(key, e.target.value);
                  onFieldChange();
                }}
              />
            ))}
          {type === "checkbox" && (
            <OptionsButton
              items={[
                {
                  text: `${checkboxText1 || "Yes"}`,
                  key,
                  value: true,
                  isActive: value === true,
                  title: `${title}` || "",
                },
                {
                  text: `${checkboxText2 || "No"}`,
                  key,
                  value: false,
                  isActive: value === false,
                  title: `${title}` || "",
                },
              ]}
              handleChange={(k, v) => {
                setField(k, v);
                onFieldChange();
                customFunc(k, v, title, value);
              }}
            />
          )}
          {type === "singlecheckbox" && (
            <Checkbox
              label={title}
              name={title}
              isChecked={value === true}
              onClick={() => {
                setField(key, !value);
                onFieldChange();
                customFunc(key, !value, title);
              }}
            />
          )}
          {type === "multivalueCheckbox" && (
            <div>
              {selectOptions.map((option) => {
                const nestedField =
                  optionsDetailsInputs && optionsDetailsInputs[option.value];
                return (
                  <div
                    key={option.value}
                    style={{ alignItems: "center", padding: "5px" }}
                    className="row no-gutters"
                  >
                    <Checkbox
                      id={option.value}
                      isChecked={value[option.value]}
                      onClick={(e) => {
                        const newValue = {
                          ...value,
                          [option.value]: e.target.checked,
                        };
                        setField(key, newValue);
                        onFieldChange();
                      }}
                      label={option.label || option.value}
                    />
                    {nestedField && (
                      <div className="col-5 col-sm-3">
                        {renderField([
                          nestedField,
                          { ...fields[nestedField], display: true },
                        ])}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {type === "radio" && (
            <div>
              {radioOptions.map((option) => {
                if (!ignoredDnataOptions.includes(option)) {
                  return (
                    <RadioButton
                      key={option}
                      inputId={option}
                      label={option}
                      isChecked={value === option}
                      onClick={() => {
                        setField(key, option);
                      }}
                    />
                  );
                } else {
                  return (
                    <div key={option} className="mb-1">
                      {option}
                    </div>
                  );
                }
              })}
            </div>
          )}
          {type === "date" && (
            <input
              type={type}
              value={value && moment(value).format("YYYY-MM-DD")}
              onChange={(e) => setField(key, e.target.value)}
              required
              className="register_field form-control"
            />
          )}
          {/* {type === "radio" && ( //<--- add this back once form dev tool is working
            <div>
              {radioOptions.map((option) => (
                <RadioButton
                  key={option.value}
                  inputId={option.value}
                  label={option.value}
                  isChecked={value === option.value}
                  onClick={() => setField(key, option)}
                />
              ))}
            </div>
          )} */}
          {type === "textarea" && (
            <TextAreaGroup
              value={value}
              rows={rows}
              onChange={(e) => {
                setField(key, e.target.value);
                onFieldChange();
              }}
            />
          )}
          {type === "select" && (
            <SelectListGroup
              id={`${formTitle}-${title.replace("", "-")}`}
              value={value}
              onChange={(e) => setField(key, e.target.value)}
              options={selectOptions}
              classes="register_field-dropdown"
            />
          )}
          {type === "heading" && (
            <HorizontalLine text={title} desc={textDescription.map((v) => v)} />
          )}
          {type === "table" && (
            <Table
              tableKey={key}
              value={value}
              setField={setField}
              tableDetails={tableDetails}
            />
          )}
          {type === "repeated" && (
            <Fragment>
              {repeatedContent[repeatedIndex].state.map((el, i) => (
                <Fragment key={i}>
                  <HorizontalLine
                    text={title}
                    desc={`${rowTitle || "Row"} ${i + 1}`}
                  />
                  <form>
                    {tableDetails.columns.map((cur, i2) => (
                      <Fragment key={cur + i2}>
                        <label className="grey-text">
                          {cur}
                          {repeatedContent[repeatedIndex].mandatory ? "*" : ""}
                        </label>
                        <input
                          onChange={(e) =>
                            repeatedContent[repeatedIndex].setValue(
                              e.target.value,
                              cur,
                              i
                            )
                          }
                          value={el[cur]}
                          className="form-control"
                        />
                      </Fragment>
                    ))}
                  </form>
                </Fragment>
              ))}
              <div>
                {
                  <button
                    disabled={repeatedContent[repeatedIndex].state.length === 1}
                    onClick={repeatedContent[repeatedIndex].removeLast}
                    className="btn btn-secondary my-2"
                  >
                    Remove Last
                  </button>
                }
                <button
                  onClick={repeatedContent[repeatedIndex].add}
                  disabled={
                    repeatedContent[repeatedIndex].state.length >=
                    maxTableRowLength
                  }
                  className="btn btn-secondary m-2 "
                >
                  {" "}
                  Add More{" "}
                </button>
              </div>
            </Fragment>
          )}
          {type === "workHistory" && (
            <Fragment>
              {workHistoryData.map((el, i) => (
                <Fragment key={i}>
                  <HorizontalLine text="Work History" desc={`Part ${i + 1}`} />
                  <form>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        margin: "1rem 0",
                      }}
                      onChange={(e) => setValue(e.target.value, "type", i)}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <input
                          style={inputStyle}
                          type="radio"
                          value="employed"
                          name="type"
                          checked={el.type === "employed"}
                        />{" "}
                        Employed
                      </div>
                      <div
                        style={{
                          padding: "5px 0",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <input
                          style={inputStyle}
                          type="radio"
                          value="ucb"
                          name="type"
                          checked={el.type === "ucb"}
                        />{" "}
                        Unemployed and claiming benefits
                      </div>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <input
                          style={inputStyle}
                          type="radio"
                          value="uncb"
                          name="type"
                          checked={el.type === "uncb"}
                        />{" "}
                        Unemployed NOT claiming benefits
                      </div>
                      <div
                        style={{
                          padding: "5px 0",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <input
                          style={inputStyle}
                          type="radio"
                          value="travelling"
                          name="type"
                          checked={el.type === "travelling"}
                        />{" "}
                        Travelling
                      </div>
                      <div
                        style={{
                          padding: "5px 0",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <input
                          style={inputStyle}
                          type="radio"
                          value="studying"
                          name="type"
                          checked={el.type === "studying"}
                        />{" "}
                        Studying
                      </div>
                      {/* <div style={{ display: "flex", alignItems: "center" }}>
                        <input
                          style={inputStyle}
                          type="radio"
                          value="self-employment"
                          name="type"
                          checked={el.type === "self-employment"}
                        />{" "}
                        Self-employment
                      </div> */}
                      {/* <div
                        style={{
                          padding: "5px 0",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <input
                          style={inputStyle}
                          type="radio"
                          value="voluntary"
                          name="type"
                          checked={el.type === "voluntary"}
                        />{" "}
                        Voluntary
                      </div> */}
                      {/* <div style={{ display: "flex", alignItems: "center" }}>
                        <input
                          style={inputStyle}
                          type="radio"
                          value="agency"
                          name="type"
                          checked={el.type === "agency"}
                        />{" "}
                        Agency
                      </div> */}
                    </div>
                    <label className="grey-text">From*</label>
                    <input
                      type="date"
                      value={el.from}
                      onChange={(e) => setValue(e.target.value, "from", i)}
                      required
                      className="form-control"
                    />
                    <label className="grey-text">To*</label>
                    <input
                      value={el.to}
                      onChange={(e) => setValue(e.target.value, "to", i)}
                      required
                      type="date"
                      className="form-control"
                    />
                    <p style={{ color: "red", fontSize: "13px" }}>
                      {validationErrorMessage[i] &&
                        validationErrorMessage[i].toAfterFromDate}
                    </p>
                    <p style={{ color: "red", fontSize: "13px" }}>
                      {validationErrorMessage[i] &&
                        validationErrorMessage[i].tooBigGap}
                    </p>
                    <p style={{ color: "red", fontSize: "13px" }}>
                      {validationErrorMessage[i] &&
                        validationErrorMessage[i].keepOrder}
                    </p>
                    <label className="grey-text">
                      Company Name {showAstrix(el)}
                    </label>
                    <input
                      onChange={(e) => setValue(e.target.value, "company", i)}
                      value={el.company}
                      className="form-control"
                    />
                    <label className="grey-text">Referee Name*</label>
                    <input
                      onChange={(e) => setValue(e.target.value, "referee", i)}
                      value={el.referee}
                      required
                      className="form-control"
                    />
                    <label className="grey-text">Address*</label>
                    <input
                      onChange={(e) => setValue(e.target.value, "address", i)}
                      value={el.address}
                      required={el.type !== "ucb" || el.type !== "uncb"}
                      className="form-control"
                    />
                    <label className="grey-text">Telephone*</label>
                    <input
                      onChange={(e) => setValue(e.target.value, "telephone", i)}
                      value={el.telephone}
                      required
                      className="form-control"
                    />
                    <label className="grey-text">Email*</label>
                    <input
                      onChange={(e) => setValue(e.target.value, "email", i)}
                      value={el.email}
                      required={el.type !== "ucb" || el.type !== "uncb"}
                      className="form-control"
                    />
                    <label className="grey-text">
                      Reason for leaving {showAstrix(el)}
                    </label>
                    <input
                      onChange={(e) => setValue(e.target.value, "reason", i)}
                      value={el.reason}
                      required={el.type !== "ucb" || el.type !== "uncb"}
                      className="form-control"
                    />
                  </form>
                </Fragment>
              ))}
              <div>
                <p style={{ color: "orange", fontSize: "14px" }}>
                  {fiveYearsErrorMessage && fiveYearsErrorMessage}
                </p>
                {
                  <button
                    disabled={workHistoryData.length === 1}
                    onClick={removeLast}
                    className="btn btn-secondary my-2"
                  >
                    Remove Last
                  </button>
                }
                <button
                  onClick={() =>
                    setWorkHistoryData([
                      ...workHistoryData,
                      {
                        from: "",
                        to: "",
                        company: "",
                        referee: "",
                        address: "",
                        telephone: "",
                        email: "",
                        reason: "",
                      },
                    ])
                  }
                  className="btn btn-secondary m-2 "
                >
                  {" "}
                  Add More{" "}
                </button>
              </div>
            </Fragment>
          )}
          {type === "workHistoryV2" && (
            <Fragment>
              <form>
                {workHistoryData.map((el, i) => (
                  <Fragment key={i}>
                    <HorizontalLine
                      text={workHistoryDataTitle || "Work History"}
                      desc={`Part ${i + 1}`}
                    />
                    {workHistoryColumns.map((rowObj) => {
                      // eslint-disable-next-line no-shadow
                      const { title, type, mandatory, format } = rowObj;
                      if (type === "text") {
                        return (
                          <Fragment>
                            <label className="grey-text">
                              {title}
                              {mandatory ? "*" : ""}
                            </label>
                            <input
                              onChange={(e) =>
                                setValue(e.target.value, title, i)
                              }
                              value={el[title]}
                              className="form-control"
                            />
                          </Fragment>
                        );
                      }
                      if (type === "date")
                        return (
                          <Fragment>
                            <label className="grey-text">
                              {title}
                              {mandatory ? "*" : ""}
                            </label>
                            <input
                              type={
                                format && format === "month" ? `month` : "date"
                              }
                              value={el[title]}
                              onChange={(e) =>
                                setValue(e.target.value, title, i)
                              }
                              required
                              className="form-control"
                            />
                          </Fragment>
                        );
                      return null;
                    })}
                    <p style={{ color: "red", fontSize: "13px" }}>
                      {workValidationErrorMessage &&
                        workValidationErrorMessage[i] &&
                        workValidationErrorMessage[i].toAfterFromDate}
                    </p>
                    <p style={{ color: "red", fontSize: "13px" }}>
                      {workValidationErrorMessage &&
                        workValidationErrorMessage[i] &&
                        workValidationErrorMessage[i].tooBigGap}
                    </p>
                    <p style={{ color: "red", fontSize: "13px" }}>
                      {workValidationErrorMessage &&
                        workValidationErrorMessage[i] &&
                        workValidationErrorMessage[i].keepOrder}
                    </p>
                  </Fragment>
                ))}
              </form>
              <div>
                <p style={{ color: "orange", fontSize: "14px" }}>
                  {xYearsWorkErrorMessage && xYearsWorkErrorMessage}
                </p>

                {
                  <button
                    disabled={workHistoryData.length === 1}
                    onClick={removeLast}
                    className="btn btn-secondary my-2"
                  >
                    Remove Last
                  </button>
                }
                <button
                  onClick={() =>
                    setWorkHistoryData([...workHistoryData, initialWorkHistory])
                  }
                  className="btn btn-secondary m-2 "
                >
                  {" "}
                  Add More{" "}
                </button>
              </div>
            </Fragment>
          )}
          {type === "addressHistoryV2" && (
            <Fragment>
              <form>
                {addressHistoryData.map((el, i) => (
                  <div key={i} className="mb-1">
                    <HorizontalLine
                      text="Address History"
                      desc={
                        i === 0 && !onlyPrevious
                          ? `Current Address`
                          : `Previous Address ${onlyPrevious ? i + 1 : i}`
                      }
                    />
                    {addressHistoryColumns.map((rowObj) => {
                      // eslint-disable-next-line no-shadow
                      const { title, type, mandatory, format } = rowObj;
                      if (type === "text") {
                        return (
                          <div className="mb-1">
                            <label className="grey-text">
                              {title}
                              {mandatory ? "*" : ""}
                            </label>
                            <input
                              onChange={(e) =>
                                setAddressHistoryValue(e.target.value, title, i)
                              }
                              value={el[title]}
                              className="form-control"
                            />
                          </div>
                        );
                      }
                      if (type === "checkbox") {
                        return (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              flexDirection: "column",
                            }}
                            className="mb-2"
                          >
                            <label className="grey-text">
                              {title}
                              {mandatory ? "*" : ""}
                            </label>
                            <input
                              type="checkbox"
                              onChange={(e) =>
                                setAddressHistoryValue(
                                  e.target.checked,
                                  title,
                                  i
                                )
                              }
                              checked={!!el[title]}
                              style={{ height: 32, width: 32 }}
                            />
                          </div>
                        );
                      }
                      if (type === "date")
                        return (
                          <div className="mb-1">
                            <label className="grey-text">
                              {title}
                              {mandatory ? "*" : ""}
                            </label>
                            <input
                              type={
                                format && format === "month" ? `month` : "date"
                              }
                              value={el[title]}
                              onChange={(e) =>
                                setAddressHistoryValue(e.target.value, title, i)
                              }
                              required
                              className="form-control"
                            />
                          </div>
                        );
                      return null;
                    })}
                    {addressValidationErrorMessage[i]
                      ? Object.values(addressValidationErrorMessage[i]).map(
                          (msg) => (
                            <p
                              key={i + msg}
                              style={{ color: "red", fontSize: "13px" }}
                            >
                              {msg}
                            </p>
                          )
                        )
                      : null}
                  </div>
                ))}
              </form>
              <div>
                <p style={{ color: "orange", fontSize: "14px" }}>
                  {addressYearsErrorMessage && addressYearsErrorMessage}
                </p>
                {
                  <button
                    disabled={addressHistoryData.length === 1}
                    onClick={addressHistoryRemoveLast}
                    className="btn btn-secondary my-2"
                  >
                    Remove Last
                  </button>
                }
                <button
                  onClick={() =>
                    setAddressHistoryData([
                      ...addressHistoryData,
                      initialAddressHistory,
                    ])
                  }
                  className="btn btn-secondary m-2 "
                >
                  {" "}
                  Add More{" "}
                </button>
              </div>
            </Fragment>
          )}
          {type === "addressHistory" && (
            <Fragment>
              {addressHistoryData.map((el, i) => (
                <Fragment key={i}>
                  <HorizontalLine
                    text="Address History"
                    desc={i === 0 ? `Current Address` : `Previous Address ${i}`}
                  />
                  <form>
                    <label className="grey-text">From (Month/Year)*</label>
                    <input
                      type="month"
                      value={el.from}
                      onChange={(e) =>
                        setAddressHistoryValue(e.target.value, "from", i)
                      }
                      required
                      className="form-control"
                    />
                    <label className="grey-text">To (Month/Year)*</label>
                    <input
                      value={i !== 0 ? el.to : moment().format("YYYY-MM")}
                      onChange={(e) =>
                        setAddressHistoryValue(e.target.value, "to", i)
                      }
                      required
                      type="month"
                      className="form-control"
                    />
                    <label className="grey-text">Line 1*</label>
                    <input
                      onChange={(e) =>
                        setAddressHistoryValue(e.target.value, "line1", i)
                      }
                      value={el.line1}
                      required
                      className="form-control"
                    />
                    <label className="grey-text">Line 2</label>
                    <input
                      onChange={(e) =>
                        setAddressHistoryValue(e.target.value, "line2", i)
                      }
                      value={el.line2}
                      className="form-control"
                    />
                    <label className="grey-text">Line 3</label>
                    <input
                      onChange={(e) =>
                        setAddressHistoryValue(e.target.value, "line3", i)
                      }
                      value={el.line3}
                      className="form-control"
                    />
                    <label className="grey-text">Town*</label>
                    <input
                      onChange={(e) =>
                        setAddressHistoryValue(e.target.value, "town", i)
                      }
                      value={el.town}
                      required
                      className="form-control"
                    />
                    <label className="grey-text">County*</label>
                    <input
                      onChange={(e) =>
                        setAddressHistoryValue(e.target.value, "county", i)
                      }
                      value={el.county}
                      required
                      className="form-control"
                    />
                    <label className="grey-text">Postcode*</label>
                    <input
                      onChange={(e) =>
                        setAddressHistoryValue(e.target.value, "postcode", i)
                      }
                      value={el.postcode}
                      required
                      className="form-control"
                    />

                    <p style={{ color: "red", fontSize: "13px" }}>
                      {addressValidationErrorMessage[i] &&
                        addressValidationErrorMessage[i].toAfterFromDate}
                    </p>
                    <p style={{ color: "red", fontSize: "13px" }}>
                      {addressValidationErrorMessage[i] &&
                        addressValidationErrorMessage[i].tooBigGap}
                    </p>
                    <p style={{ color: "red", fontSize: "13px" }}>
                      {addressValidationErrorMessage[i] &&
                        addressValidationErrorMessage[i].toExceedsFrom}
                    </p>
                  </form>
                </Fragment>
              ))}
              <div>
                <p style={{ color: "orange", fontSize: "14px" }}>
                  {addressYearsErrorMessage && addressYearsErrorMessage}
                </p>
                {
                  <button
                    disabled={addressHistoryData.length === 1}
                    onClick={addressHistoryRemoveLast}
                    className="btn btn-secondary my-2"
                  >
                    Remove Last
                  </button>
                }
                <button
                  onClick={() => {
                    if (addressHistoryData.length < 6) {
                      setAddressHistoryData([
                        ...addressHistoryData,
                        {
                          line1: "",
                          line2: "",
                          line3: "",
                          town: "",
                          county: "",
                          postcode: "",
                          from: "",
                          to: "",
                        },
                      ]);
                    }
                  }}
                  disabled={addressHistoryData.length > 5}
                  className="btn btn-secondary m-2 "
                >
                  {" "}
                  Add More{" "}
                </button>
              </div>
            </Fragment>
          )}
        </div>
      );
    }
    return <Fragment key={key} />;
  };

  const disableSubmit = () => {
    // todo tidy up
    const chkWrkHistory = shouldDisable || !isSubmittable;
    const chkRepeated =
      repeatedDisable && repeatedDisable.every((b) => b === false);
    // if (checkWorkHistory && checkRepeatedDisabled)
    //   return chkWrkHistory || !chkRepeated || loading;
    // if (checkWorkHistory) return chkWrkHistory || loading;
    if (checkRepeatedDisabled) return !chkRepeated || !isSubmittable;
    return !isSubmittable || loading || shouldDisable || shouldDisableAddress;
  };

  return (
    <div className="container d-flex flex-column align-items-center justify-content-center pt-2 tw-bg-white">
      <Header
        showDescription
        classes="tw-w-100"
        description={description}
        disableStyle={true}
      >
        <h2 className="tw-w-full">{formTitle}</h2>
      </Header>
      <p className={styles.FormCreatorV2__required}>{helper}</p>
      <div className="col-sm-11 col-md-8 col-lg-6 mb-2 section-body p-1 mx-1 mx-sm-0">
        {Object.entries(fields).map(renderField)}
        {customHtml}
        <div className="d-flex justify-content-end mt-1 mt-sm-0 flex-sm-row flex-column-reverse">
          <button
            className="btn btn-outline-dark text-dark hover-text-white col-12 col-sm-3 col-lg-3 mt-1 mt-sm-0"
            onClick={handleCancel}
          >
            Cancel
          </button>

          {isCustomSaveButton ? (
            <button
              className="btn btn-primary col-12 col-sm-3 col-lg-3 ml-sm-1 "
              disabled={!isSubmittable || disableSave}
              onClick={handleSave}
            >
              Save
            </button>
          ) : (
            <button
              className="btn btn-primary col-12 col-sm-3 col-lg-3 ml-sm-1  mt-1 mt-sm-0 blah"
              disabled={
                submitDisabled === true
                  ? true
                  : submitSingleForm
                  ? false
                  : disableSubmit()
              }
              onClick={submit}
            >
              {["royalMail", "driving"].includes(onboardingBranch) ||
              useSubmitText
                ? "Submit"
                : "Save"}
              {loading && <Spinner />}
            </button>
          )}
          {submitSingleForm ? (
            <button
              className="btn btn-primary col-12 col-sm-3 col-lg-3 ml-sm-1 "
              disabled={disableSubmit()}
              onClick={handleSingleSubmit}
            >
              Submit
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

FormCreatorV2.propTypes = {
  formTitle: string,
  description: string,
  fields: any,
  setField: any,
  handleSubmit: func,
  handleCancel: func,
  onboardingBranch: string,
  helper: string,
  customHtml: any,
  handleSave: func,
  isCustomSaveButton: bool,
  disableSave: bool,
  submitDisabled: bool,
  loading: bool,
  workHistoryData: array,
  setValue: func,
  removeLast: func,
  validationErrorMessage: string,
  fiveYearsErrorMessage: string,
  setWorkHistoryData: func,
  shouldDisable: bool,
  shouldDisableAddress: bool,
  repeatedContent: array,
  repeatedDisable: array,
  addressHistoryData: array,
  setAddressHistoryData: func,
  addressYearsErrorMessage: string,
  xYearsWorkErrorMessage: string,
  workValidationErrorMessage: array,
  addressValidationErrorMessage: string,
  setAddressHistoryValue: func,
  addressHistoryRemoveLast: func,
  submitSingleForm: bool,
  handleSingleSubmit: func,
  useSubmitText: bool,
  workHistoryDataTitle: string,
};

export default FormCreatorV2;
